import { useCallback, useEffect, useRef, useState } from 'react';

import { AlertSuccessIcon } from '@braskai/icons';
import { Button, Modal, ModalActions, ModalContent, ModalWrapper, Typography } from '@braskai/ui-kit';

import { Stack } from '@mui/material';

import { DemoBookingCalendarModal } from '@/entities/demoBooking';
import { useBookACallStore, useLeadSurvey } from '@/entities/leadSurvey';
import { useCurrentUser } from '@/entities/user/currentUser';

import { BOOK_A_DEMO_MEETING_LINK } from '@/shared/constants/docLinks';
import { enqueueAppSnackbar } from '@/shared/lib/snackbar';
import { MixpanelEvents } from '@/shared/thirdParty';
import { AlertType } from '@/shared/types/Alert';

const BookACallModalComponent = () => {
  const close = useBookACallStore((state) => state.close);
  const { markBooked, markSkipped } = useLeadSurvey();
  const { data: user } = useCurrentUser();

  const [isBooked, setIsBooked] = useState(false);
  const bookedRef = useRef(false);

  // The modal only mounts when open (ModalWrapper) → fire "opened" once per open.
  useEffect(() => {
    MixpanelEvents.bookACallOpened();
  }, []);

  // Idempotent: a leaked/duplicate postMessage can't double-fire the toast or the booked write.
  const handleBooked = useCallback(() => {
    if (bookedRef.current) {
      return;
    }

    bookedRef.current = true;
    setIsBooked(true);
    markBooked();
    MixpanelEvents.bookACallBooked();
    enqueueAppSnackbar('Your demo is booked — see you there!', { variant: AlertType.Success });
  }, [markBooked]);

  const handleDismiss = useCallback(
    (source: 'close' | 'maybeLater') => {
      if (!bookedRef.current) {
        markSkipped();
        MixpanelEvents.bookACallSkipped();

        if (source === 'maybeLater') {
          MixpanelEvents.bookACallMaybeLaterClicked();
        } else {
          MixpanelEvents.bookACallCloseClicked();
        }
      }

      close();
    },
    [close, markSkipped],
  );

  const handleClose = useCallback(() => handleDismiss('close'), [handleDismiss]);
  const handleMaybeLater = useCallback(() => handleDismiss('maybeLater'), [handleDismiss]);

  if (isBooked) {
    return (
      <Modal size="sm" open onClose={handleClose}>
        <ModalContent>
          <Stack
            sx={{
              '@keyframes bookACallPop': {
                from: { opacity: 0, transform: 'scale(0.85)' },
                to: { opacity: 1, transform: 'scale(1)' },
              },
              '@keyframes bookACallRise': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              py: 24,
              textAlign: 'center',
            }}
            alignItems="center"
            gap={16}
          >
            <AlertSuccessIcon
              height={64}
              style={{ animation: 'bookACallPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
              width={64}
            />

            <Stack alignItems="center" gap={6}>
              <Typography
                color="grey.900"
                sx={{ animation: 'bookACallRise 0.4s ease-out 0.1s both' }}
                variant="title-2-sb"
              >
                Your demo is booked!
              </Typography>

              <Typography
                color="grey.500"
                maxWidth={380}
                sx={{ animation: 'bookACallRise 0.4s ease-out 0.18s both' }}
                variant="body-1"
              >
                Check your inbox, accept the invite, and we&apos;ll see you soon!
              </Typography>
            </Stack>
          </Stack>
        </ModalContent>

        <ModalActions>
          <Button data-testid="book-a-call-modal-done" size="lg" variant="primary" fullWidth onClick={handleClose}>
            Done
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <DemoBookingCalendarModal
      secondaryAction={
        <Stack alignItems="center" paddingBottom={4} paddingTop={12}>
          <Button
            data-testid="book-a-call-maybe-later"
            size="md"
            sx={{ width: 200 }}
            variant="outlined"
            onClick={handleMaybeLater}
          >
            Maybe later
          </Button>
        </Stack>
      }
      email={user?.email}
      meetingUrl={BOOK_A_DEMO_MEETING_LINK}
      onBooked={handleBooked}
      onClose={handleClose}
      onLoadError={MixpanelEvents.bookACallEmbedFailed}
    />
  );
};

/**
 * Book-a-Call modal. Skippable (close button + "Maybe later"). On a confirmed HubSpot
 * booking → confirmation state + dashboard toast + `markBooked()`; on dismiss → `markSkipped()`.
 */
export const BookACallModal = () => (
  <ModalWrapper hook={useBookACallStore}>
    <BookACallModalComponent />
  </ModalWrapper>
);
